import React, { useEffect, useLayoutEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Lightweight 5-step guided tour used by the Slip Tracker Audit page.
 *
 * Positioning strategy: on each step, we find the element matching
 * `steps[i].selector`, read its bounding rect, and render a fixed-position
 * tooltip below (or above, if it would overflow) the target. A translucent
 * dark backdrop dims the rest of the page but the target element is punched
 * out by a bright ring so the user's eye is drawn to it.
 *
 * No third-party library — keeps bundle size flat and avoids version churn.
 */
const Tour = ({ open, step, onStep, onClose, storageKey, steps }) => {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!open || step == null) { setRect(null); return; }
    const s = steps[step];
    if (!s) return;
    const el = document.querySelector(s.selector);
    if (!el) { setRect(null); return; }
    // Scroll the target into view before measuring
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const measure = () => setRect(el.getBoundingClientRect());
    const t = setTimeout(measure, 350);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, step, steps]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  if (!open || step == null) return null;
  const s = steps[step];
  const last = step === steps.length - 1;

  const next = () => (last ? finish() : onStep(step + 1));
  const prev = () => step > 0 && onStep(step - 1);
  const finish = () => {
    if (storageKey) localStorage.setItem(storageKey, '1');
    onClose();
  };

  // Position tooltip below the target unless too close to the bottom
  let top = 24, left = 24, arrowSide = 'top';
  const TT_W = 340; // tooltip width
  const MARGIN = 12;
  if (rect) {
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow > 200) {
      top = rect.bottom + MARGIN;
      arrowSide = 'top';
    } else {
      top = Math.max(MARGIN, rect.top - 200);
      arrowSide = 'bottom';
    }
    left = Math.min(
      Math.max(MARGIN, rect.left + rect.width / 2 - TT_W / 2),
      window.innerWidth - TT_W - MARGIN,
    );
  }

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="stba-tour">
      {/* Dim + highlight */}
      <div className="absolute inset-0 bg-slate-900/50 pointer-events-auto" onClick={finish} />
      {rect && (
        <div
          className="absolute rounded-md ring-4 ring-emerald-400 pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 9999px rgba(15,23,42,0.5)',
          }}
        />
      )}
      {/* Tooltip */}
      <div
        className="absolute bg-white border border-emerald-200 rounded-lg shadow-2xl p-4 pointer-events-auto"
        style={{ top, left, width: TT_W }}
        data-testid={`stba-tour-step-${step}`}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">
            Step {step + 1} of {steps.length}
          </p>
          <button type="button" onClick={finish} className="text-slate-400 hover:text-slate-700"
            data-testid="stba-tour-close" aria-label="Close tour">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <h3 className="text-[13px] font-semibold text-slate-900">{s.title}</h3>
        <p className="text-[12px] text-slate-700 mt-1.5 leading-relaxed">{s.body}</p>
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
          <button type="button" onClick={prev} disabled={step === 0} data-testid="stba-tour-prev"
            className="text-[11px] px-2 py-1 rounded flex items-center gap-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition ${i === step ? 'bg-emerald-500 w-3' : 'bg-slate-300'}`} />
            ))}
          </div>
          <button type="button" onClick={next} data-testid="stba-tour-next"
            className="text-[11px] px-3 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1">
            {last ? 'Got it' : (<>Next <ChevronRight className="w-3 h-3" /></>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tour;
